/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useState, useEffect } from "react";
/* eslint-enable @typescript-eslint/no-unused-vars */

// OFFICE DEMO:
// https://codesandbox.io/p/sandbox/react-doc-viewer-demo-zvk4j?file=%2Fsrc%2FApp.js%3A19%2C5

// Main component
// DocViewerRenderers, TXTRenderer, PDFRenderer, MSDocRenderer, MSGRenderer, HTMLRenderer, BMPRenderer, TIFFRenderer, PNGRenderer, JPGRenderer
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";

import Iframe from "react-iframe";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";

// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts";
// Reducers:
import { getConfig, getLng, getUser } from "../../libs/reducer.ts"; // getTheme

interface IHeaderOverride {
  overrideComponent: () => React.ReactElement<any, any> | null
}

interface IDocument {
  uri?:string;
  fileType?:string;
  fileName?:string;
  fileData?:string | ArrayBuffer | Blob;
  // eslint-disable-next-line react/boolean-prop-naming
  allowEditOfficeFiles?:boolean;
}

interface IHeaderConfig {
  disableHeader?:	boolean;
  disableFileName?:	boolean;
  retainURLParams?:	boolean;
  overrideComponent?:	IHeaderOverride;
}

interface IConfig {
  header?:	IHeaderConfig;
}

interface IMainState {
  currentFileNo: number;
  documents: IDocument[];
  documentLoading?: boolean;
  currentDocument?: IDocument;
  rendererRect?: DOMRect;
  config?: IConfig;
}

interface FileLoaderComplete {
  fileReader:FileReader;
}

interface FileLoaderFuncProps {
  documentURI:string;
  signal:AbortSignal;
  fileLoaderComplete:FileLoaderComplete;
}

type FileLoaderFunction = (props:FileLoaderFuncProps) => void;

interface DocRendererProps {
  mainState:	IMainState
}

interface DocRenderer extends FC<DocRendererProps> {
  mainState?: IMainState;
  fileTypes: string[];
  weight?: number;
  fileLoader?: FileLoaderFunction | null | undefined;
}

// ==========================

type viewerProps = {
  viewerKey:string,
  document:IDocument,
  header?:IHeaderOverride,
  className?:string,
  style?:React.CSSProperties,
};

const { rz } = window;

const LoadingSpinner = (loader:string) => (<div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}><div className={loader}></div></div>);

// Markdown-viewer:
const MDrenderer:DocRenderer = ({mainState: {currentDocument}}:{mainState:IMainState}) => {
  // const theme = useAppSelector(getTheme);

  const [loading, setLoading] = useState<boolean>(true);
  const [content, setContent] = useState<string>("");

  const codeComponents = useMemo(() => ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        // style={theme === "light" ? light : dark}
        <SyntaxHighlighter PreTag="div" language={match[1]} {...props}>
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  }), []);

  useEffect(() => {
    if (!loading) { return }
    if (currentDocument?.uri && !(currentDocument.fileData && rz.isString(currentDocument.fileData))) {
      fetch(currentDocument.uri, {method: "GET"}).then((resp) => { // credentials: "include", mode: "same-origin"
        if (!resp.ok) { setLoading(false); return }
        // resp.text().then((str) => { setContent(str); setLoading(false) });
        resp.blob().then((blb) => {
          // const file = window.URL.createObjectURL(blb); window.location.assign(file); // To download file
          // const blob = new Blob([string], {type: "image/jpeg"}); // Blob from string
          blb.text().then((str) => {
            setContent(str); setLoading(false);
          });
        });
      }).catch((err) => { console.log(err); setLoading(false) });
    }
    else {
      if (rz.isString(currentDocument?.fileData)) {
        let str = currentDocument?.fileData;
        if ((/^data:.+;base64,/igm).test(currentDocument?.fileData as string)) {
          str = (str as string).replace((/^data:.+;base64,/igm), "");
          str = atob(str as string);
        }
        setContent(str as string);
      }
      setLoading(false);
    }
  }, [currentDocument?.fileData, currentDocument?.uri, loading]);

  if (!currentDocument) return null;

  return (
    <div key="rz-md-viewer" id="rz-md-viewer" className="rz-width-expand">
      {loading
        ? LoadingSpinner("loader06")
        : <Markdown key={"rz-md-renderer"} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}
            components={codeComponents}
          >
            {content}
          </Markdown>
      }
    </div>
  );
};
MDrenderer.fileTypes = ["md", "json", "jsn", "txt", "xml"];
MDrenderer.weight = 1;

// ==========================

/* const saveOfficeFileForce = (e) => {
  const iframe = document.getElementById("rz-office-docs-viewer-iframe");
  // @ts-ignore
  if (iframe) { iframe.contentWindow.postMessage("Save", "*") }
  e.preventDefault(); e.returnValue = "";
  setTimeout(() => { window.close() }, 1000);
}; */

// Office-Online viewer / editor:
const MSDocRenderer:DocRenderer = ({mainState: {currentDocument}}:{mainState:IMainState}) => {
  // const theme = useAppSelector(getTheme);
  const cfg = useAppSelector(getConfig);
  const lng = useAppSelector(getLng);
  const curUser = useAppSelector(getUser);

  const [loading, setLoading] = useState<boolean>(true);
  const [owaURI, setOwaURI] = useState("");
  const [wopiMethods, setWopiMethods] = useState({});
  const [fileToken, setFileToken] = useState("");
  const [error, setError] = useState("");

  const srvLink = useMemo(() => (cfg.isDev ? cfg.links.devUrl || "http://localhost:91" : (cfg.appname === "smartteamdev" ? cfg.links.portalUrl : cfg.links.srvbgUrl)), [cfg.isDev, cfg.links, cfg.appname]);
  const owaLink = useMemo(() => ((cfg.links && cfg.links.owaAddress) || ""), [cfg.links]);
  const fileName = useMemo(() => {
    let fname = rz.strFileName(currentDocument?.fileName || "");
    if (!fname) { const uri = decodeURIComponent(currentDocument?.uri || ""); fname = uri.slice(uri.lastIndexOf("/") + 1, uri.length - 1) }
    return fname;
  }, [currentDocument]);

  useEffect(() => {
    // Debug
    console.log({currentDocument, fileName, owaURI, srvLink, owaLink, fileToken, loading, error});

    if (!loading) { return }
    if (!currentDocument || !currentDocument.uri) { setError("Document URI is not defined"); setLoading(false); return }
    if (owaURI) { setLoading(false); return }

    if (owaLink) { // Using Office Online in the Local Network
      if (fileToken) {
        const fileExt = rz.strFileExtension(fileName);
        if (fileExt && wopiMethods && wopiMethods[fileExt] && wopiMethods[fileExt].length) {
          const mm = wopiMethods[fileExt].find((ii) => (ii[0] === (currentDocument.allowEditOfficeFiles ? "edit" : "view"))) || wopiMethods[fileExt][0];
          const wopiMethod = mm[1];

          const WOPISrc = encodeURIComponent(srvLink + "/wopi/files/" + fileToken);
          let editorLink = ((wopiMethod || "").startsWith(owaLink) || (wopiMethod || "").startsWith("http") ? "" : owaLink) + wopiMethod + (~wopiMethod.indexOf("?") ? "&" : "?") + "WOPISrc=" + WOPISrc;
          editorLink += "&access_token=it-needs-only-file-id";
          // editorLink += "&edit=" + (currentDocument.allowEditOfficeFiles ? "1" : "0");
          editorLink += "&title=" + fileName;
          editorLink += "&lang=ru" + (lng === "ru" ? "ru" : "en");
          editorLink += "&revisionhistory=1";
          editorLink += "&closebutton=1";
          // ******************************
          setOwaURI(editorLink);
          // Нерабочая у меня ссылка:
          // eslint-disable-next-line max-len
          // https://owa.smartteam.dev/we/wordviewerframe.aspx?WOPISrc=https://app.smartteam.dev/wopi/files/Ir7RJv4fZO9bFdswmEHwELzE&access_token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmaWxlSWQiOiJJcjdSSnY0ZlpPOWJGZHN3bUVId0VMekUiLCJ1c2VySWQiOiJ0eWFnYWV2LnAiLCJwZXJtaXNzaW9ucyI6InJlYWRvbmx5IiwiaWF0IjoxNzQ3MDQxMzA2LCJleHAiOjE3NDcwODQ1MDZ9.6yyhIdhAiJ7P-SjjCin_hQR-tsW3rvWFgAxS8zyIDWQ&edit=0
          // Рабочая ссылка из NextCloud:
          // eslint-disable-next-line max-len
          // https://owa.smartteam.dev/wv/wordviewerframe.aspx?ui=ru-Ru&WOPISrc=https://cloud.smartteam.dev/index.php/apps/officeonline/wopi/files/3271515_oc70j2f4y6nm&title=Договор_НормаСофт_NormaCS.docx&lang=ru&closebutton=1&revisionhistory=1

          // Что отвечает мой сервер на запрос файла:
          // https://app.smartteam.dev/wopi/files/M6q8PymK49m8R6FrWnhymPk0
          // {
          //   "AllowExternalMarketplace": true,
          //   "BaseFileName": "Карта контрагента ИП Тягаев Павел Сергеевич.docx",
          //   "BreadcrumbBrandName": "smartteamdev",
          //   "BreadcrumbBrandUrl": "https://app.smartteam.dev",
          //   "BreadcrumbDocName": "Карта контрагента ИП Тягаев Павел Сергеевич.docx",
          //   "BreadcrumbFolderName": "WopiStorage",
          //   "BreadcrumbFolderUrl": "https://app.smartteam.dev",
          //   "HostEditUrl": "https://app.smartteam.dev?action_url=https%3A%2F%2Fowa.smartteam.dev%2Fwe%2Fwordeditorframe.aspx%3FWOPISrc%3Dhttps%253A%252F%252Fapp.smartteam.dev%252Fwopi%252Ffiles%252FM6q8PymK49m8R6FrWnhymPk0",
          //   "HostViewUrl": "https://app.smartteam.dev?action_url=https%3A%2F%2Fowa.smartteam.dev%2Fwv%2Fwordviewerframe.aspx%3FWOPISrc%3Dhttps%253A%252F%252Fapp.smartteam.dev%252Fwopi%252Ffiles%252FM6q8PymK49m8R6FrWnhymPk0",
          //   "LastModifiedTime": "2025-04-19T14:32:05.838Z",
          //   "OwnerId": "Тягаев Павел Сергеевич",
          //   "ReadOnly": false,
          //   "Size": 22917,
          //   "SupportsCoauth": true,
          //   "SupportsCobalt": false,
          //   "SupportsDeleteFile": true,
          //   "SupportsExtendedLockLength": true,
          //   "SupportsGetLock": true,
          //   "SupportsLocks": true,
          //   "SupportsRename": true,
          //   "SupportsUpdate": true,
          //   "UserCanRename": true,
          //   "UserCanWrite": true,
          //   "UserFriendlyName": "Тягаев Павел Сергеевич",
          //   "UserId": "tyagaev.p",
          //   "Version": "1745073125838.2034"
          // }
          // ******************************
          setLoading(false);
        }
        else { // Using External Office Online - if we cant get wopiMethods[fileExt]
          // @ts-ignore
          setOwaURI(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentDocument.uri)}`);
          setLoading(false);
        }
      }
      else { // Get access token for current user
        const data = {documentUri: currentDocument.uri, allowEditOfficeFiles: currentDocument.allowEditOfficeFiles};
        fetch(srvLink + "/wopi/getaccesstoken", {method: "POST", credentials: "include", mode: "same-origin", body: JSON.stringify(data), headers: {"Content-Type": "application/json;charset=utf-8"}}).then(async (res) => {
          const contentType = res.headers.get("content-type");
          if (contentType && !~contentType.indexOf("application/json")) {
            const txt = await res.text();
            setError(txt); setLoading(false);
            return;
          }
          const jsn = await res.json();
          // Debug
          console.log({wopi_server_ans: jsn});
          if (!jsn || jsn.err) { setError((jsn && jsn.err) || "UNKNOWN ERROR"); setLoading(false) }
          else if (jsn.fileToken) { setFileToken(jsn.fileToken); setWopiMethods(jsn.wopiMethods) }
          else { // Using External Office Online - if we cant get 'accessToken' or 'fileToken'
            // @ts-ignore
            setOwaURI(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentDocument.uri)}`);
            setLoading(false);
          }
        }).catch((err) => {
          console.log(err); setError("fetch('" + srvLink + "/wopi/getaccesstoken') -> " + rz.errToString(err)); setLoading(false);
        });
      }
    }
    else { // External Office Online:
      setOwaURI(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentDocument.uri)}`);
      setLoading(false);
    }
  }, [currentDocument, loading, fileToken, owaURI, owaLink, srvLink, curUser, lng, error, fileName, wopiMethods]);

  /* useEffect(() => {
    document.addEventListener("beforeunload", saveOfficeFileForce);
    return () => {
      document.removeEventListener("beforeunload", saveOfficeFileForce);
    }
  }, []); */

  if (!currentDocument) return null;

  return (
    <div key="rz-office-docs-viewer-container" id="rz-office-docs-viewer" className={"rz-width-expand" + (error ? " uk-flex uk-flex-center uk-flex-middle" : "")} style={{height: "calc(100vh - 55px)", width: "100%"}}>
      {loading
        ? LoadingSpinner("loader06")
        : (error
          ? <div key="rz-office-docs-viewer-error" id="rz-office-docs-viewer-error" className="uk-text-danger uk-text-large">{error}</div>
          : <Iframe id={"rz-office-docs-viewer-iframe"}
              url={owaURI}
              width={"100%"} height={"100%"} frameBorder={0} overflow={"hidden"} // allowFullScreen={true}
              sandbox={rz.arrGetUnique("allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox".split(" "), true, true, true)} // allow-downloads allow-modals
            />
        )
      }
    </div>
  );
};
MSDocRenderer.fileTypes = [
  "odt", "application/vnd.oasis.opendocument.text",
  "doc", "application/msword",
  "docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/octet-stream",
  "xls", "application/vnd.ms-excel",
  "xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "ppt", "application/vnd.ms-powerpoint",
  "pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"
];
MSDocRenderer.weight = 1;

// ==========================

const RZDocViewer:FC<viewerProps> = ({viewerKey, document, header, className, style}) => {
  const cfg = useAppSelector(getConfig);

  const prgLink = useMemo(() => (cfg.isDev ? cfg.links.devUrl || "http://localhost:91" : (cfg.appname === "smartteamdev" ? cfg.links.portalUrl : cfg.links.srvbgUrl)), [cfg.appname, cfg.isDev, cfg.links.devUrl, cfg.links.portalUrl, cfg.links.srvbgUrl]);
  const docLink = useMemo(() => (document && document.uri && (document.uri.startsWith(prgLink) ? document.uri : prgLink + document.uri)), [prgLink, document]);
  const allowEditOfficeFiles = useMemo(() => (document && document.allowEditOfficeFiles), [document]);
  const fileName = useMemo(() => {
    let fname = rz.strFileName(document?.fileName || "");
    if (!fname) { const uri = decodeURIComponent(document?.uri || ""); fname = uri.slice(uri.lastIndexOf("/") + 1, uri.length - 1) }
    return fname;
  }, [document]);
  const fileData = useMemo(() => ((document.fileData || "")), [document]);

  const docParams = useMemo<IDocument[]>(() => {
    const docs:IDocument[] = [];
    const fileExt = rz.strFileExtension(fileName);
    const idoc:IDocument = {uri: docLink, fileName, allowEditOfficeFiles};
    if (rz.arrContains(["md", "html"], fileExt) && fileData) { idoc.fileData = fileData }

    docs.push(idoc);

    // if (document.uri) { docs.push({uri: document.uri, fileType: (~document.uri.indexOf("/ui/avatar/") ? "jpg" : document.fileType)}) }
    // else if (document.fileData) { // && !rz.arrContains(["doc", "docx", "xls", "xlsx", "xlsm", "ppt", "pptx", "pdf"], document.fileType)
    //  if (rz.isString(document.fileData)) {
    // let str = document.fileData;
    // if ((/^data:.+;base64,/igm).test(document.fileData as string)) {
    // str = (str as string).replace((/^data:.+;base64,/igm), "");
    // str = atob(str as string);
    // }
    //    docs.push({fileData: document.fileData, fileType: document.fileType});
    //  }
    //  else {
    //    docs.push({fileData: document.fileData, fileType: document.fileType});
    //  }
    // }
    return docs;
  }, [allowEditOfficeFiles, docLink, fileName, fileData]);

  const docViewerConfig = useMemo(() => ({
    header: header || {disableFileName: true},
    pdfVerticalScrollByDefault: true,
    pdfZoom: {
      defaultZoom: 1.2, // 1 as default
      zoomJump: 0.3 // 0.1 as default
    },
    disableTextLayer: true,
    pdf: {disableTextLayer: true},
    csvDelimiter: ","
  }), [header]);

  // console.log({docParams});

  return (
    <DocViewer key={viewerKey}
      config={docViewerConfig}
      className={className} style={style}
      // @ts-ignore
      documents={docParams}
      // @ts-ignore
      pluginRenderers={[...DocViewerRenderers, MDrenderer, MSDocRenderer]}
    />
  )
};

export default RZDocViewer;
